import { useRef, useState, type ReactNode } from 'react';
import { C, COULEUR_DU_JOUR, DEGRADE_ACTION } from '../../theme';
import { Cadeau, ChevronBas, Cloche } from '../../icones';
import { Repas } from './Repas';
import { MaSemaine } from './MaSemaine';
import { Titre, SousTitreSection, Etiquette, Squelette, Panne, Vide, Case } from '../composants/base';
import { api } from '../../api/routes';
import { useDonnees, messageDErreur } from '../../api/useDonnees';
import { useCocher, type Basculer } from '../cocher';
import {
  aujourdhuiDans,
  dansCombienDeTemps,
  heureDe,
  jourAbrege,
  jourDecale,
  jourEnToutesLettres,
} from '../format';
import { InsigneCercle } from '../composants/InsigneCercle';
import { AjouterElement } from '../composants/AjouterElement';
import { LigneElement } from '../composants/LigneElement';
import { BoutonDeuxGestes } from '../composants/BoutonDeuxGestes';
import { FormulaireEvenement } from '../composants/FormulaireEvenement';
import { FormulaireTache } from '../composants/FormulaireTache';
import { FormulaireRappel } from '../composants/FormulaireRappel';
import { ActionsLigne } from '../composants/ActionsLigne';
import {
  CaseDeSelection,
  cleDe,
  useSelection,
  type GenreSelectionnable,
  type Selection,
} from '../composants/Selection';
import type {
  ChampsCercle,
  EcheanceAVenir,
  Evenement,
  JourPlanning,
  ListeCochable,
  MembreFoyer,
  Rappel,
  Tache,
} from '../../api/types';

const JOURS_AFFICHES = 7;

/**
 * La couleur du jour, FIXE (lundi est toujours violet) : les jours se distinguent d'un
 * coup d'oeil — la meme intuition que les pastilles de couleur que Marianne met dans ses
 * propres plannings. La liste vit dans le theme : Aujourd'hui l'utilise aussi, et deux
 * copies divergeraient un jour en silence.
 */
function couleurDuJour(jour: string): string {
  return COULEUR_DU_JOUR[new Date(`${jour}T12:00:00Z`).getUTCDay()]!;
}

export function Planning({
  fuseau,
  utilisateurId,
  version,
  signaler,
  rafraichir,
  ouvrirCompris,
}: {
  fuseau: string;
  utilisateurId: string;
  version: number;
  signaler: (message: string) => void;
  rafraichir: () => void;
  ouvrirCompris: (elementId: string) => void;
}) {
  // La semaine des rendez-vous et la semaine de la cuisine partagent l'emplacement :
  // un commutateur en haut, la barre du bas garde ses cinq boutons (maquette validee).
  const [vue, setVue] = useState<'planning' | 'repas' | 'semaine'>('planning');
  // « Plus tard » se replie, et le choix SURVIT a la navigation (par appareil).
  const [plusTardOuvert, setPlusTardOuvert] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem('colette.plus-tard-replie') !== 'oui';
    } catch {
      return true;
    }
  });
  /**
   * Le repli des routines, GLOBAL et retenu — meme motif que « Plus tard ».
   *
   * Global : deplier lundi et retrouver mardi replie serait deroutant, c'est le meme
   * geste de lecture. Retenu : le porteur a cree une routine expres pour la voir et ne
   * l'a pas trouvee (2026-08-28).
   */
  const [routinesOuvertes, setRoutinesOuvertes] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem('colette.routines-depliees') === 'oui';
    } catch {
      return false;
    }
  });
  const basculerRoutines = () => {
    setRoutinesOuvertes((ouvert) => {
      try {
        window.localStorage.setItem('colette.routines-depliees', ouvert ? 'non' : 'oui');
      } catch {
        // Stockage indisponible (navigation privee) : le choix vaut pour la session.
      }
      return !ouvert;
    });
  };

  const selecteur = useSelection();

  const basculerPlusTard = () => {
    setPlusTardOuvert((ouvert) => {
      try {
        window.localStorage.setItem('colette.plus-tard-replie', ouvert ? 'oui' : 'non');
      } catch {
        // Stockage indisponible (navigation privee) : le choix vaut pour la session.
      }
      return !ouvert;
    });
  };
  const debut = aujourdhuiDans(fuseau);
  const fin = jourDecale(debut, JOURS_AFFICHES - 1);

  const etat = useDonnees(
    async (signal) => {
      const [planning, foyer] = await Promise.all([
        api.planning({ du: debut, au: fin }, signal),
        // Les membres traduisent les cercles en prenoms (« Partage avec Marianne »).
        api.foyer(signal),
      ]);
      return { ...planning, membres: foyer.household.members };
    },
    `${debut}:${version}`,
  );
  // Ce qui depasse la fenetre : la reponse a « et apres ? ». En MEILLEUR EFFORT — si
  // cette liste tombe en panne, le planning des sept jours reste entier.
  const aVenir = useDonnees(
    (signal) => api.aVenir(jourDecale(debut, JOURS_AFFICHES), signal),
    `${debut}:${version}`,
  );
  const { basculer } = useCocher(signaler, rafraichir);
  // Une ref par section de jour : la bande defile vers elles au toucher.
  const refsSections = useRef(new Map<string, HTMLDivElement>());
  // La bande sticky porte une ombre des qu'on a defile : elle « flotte » sur le contenu.
  const [aDefile, setADefile] = useState(false);

  // La COQUILLE de l'ecran, reprise par les etats d'attente.
  //
  // Sans elle, le squelette — qui ne prend que sa hauteur propre — laissait la barre de
  // navigation REMONTER, et le bouton rond du micro, qui deborde de 30 px au-dessus
  // d'elle, se retrouvait en plein milieu de l'ecran (releve par le porteur le
  // 2026-08-28). Les autres etats d'attente (Chargement, Panne) portent deja `flex: 1`.
  const coquille = (contenu: ReactNode) => (
    <div
      style={{
        flex: '1 1 0',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        padding: '30px 22px 16px 22px',
      }}
    >
      {contenu}
    </div>
  );

  if (vue === 'planning' && etat.chargement && etat.donnees === null) {
    return coquille(<Squelette lignes={4} texte="Chargement du planning…" />);
  }
  if (vue === 'planning' && etat.erreur !== null && etat.donnees === null) {
    return <Panne message={etat.erreur} onReessayer={etat.recharger} />;
  }

  const jours = etat.donnees?.days ?? [];
  const sansDate = etat.donnees?.unscheduledTasks ?? [];
  const listes = etat.donnees?.listes ?? [];
  const avecContenu = new Set(jours.map((j) => j.date));

  const bande = Array.from({ length: JOURS_AFFICHES }, (_, i) => jourDecale(debut, i));

  /** Defile vers la section du jour — ou la premiere qui suit : aucun tap mort. */
  const allerAuJour = (jour: string) => {
    const cible = jours.find((j) => j.date >= jour);
    if (cible === undefined) return;
    const section = refsSections.current.get(cible.date);
    if (section === undefined) return;
    let reduit = false;
    try {
      reduit = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch {
      // matchMedia absent (vieux navigateur) : le defilement doux est un bon defaut.
    }
    section.scrollIntoView({ behavior: reduit ? 'auto' : 'smooth', block: 'start' });
  };

  const commutateur = (
    <div
      style={{
        position: 'relative',
        display: 'flex',
        background: C.carte,
        border: `1px solid ${C.bordure}`,
        borderRadius: 999,
        padding: 4,
        gap: 4,
      }}
    >
      {/* Le fond qui glisse d'un onglet a l'autre : le mouvement dit ou l'on va. */}
      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: 4,
          bottom: 4,
          left: 4,
          width: 'calc(33.333% - 5.33px)',
          borderRadius: 999,
          background: DEGRADE_ACTION,
          transform:
            vue === 'planning'
              ? 'translateX(0)'
              : vue === 'repas'
                ? 'translateX(calc(100% + 4px))'
                : 'translateX(calc(200% + 8px))',
          transition: 'transform 0.22s ease-out',
        }}
      />
      {(['planning', 'repas', 'semaine'] as const).map((choix) => (
        <button
          key={choix}
          type="button"
          aria-pressed={vue === choix}
          onClick={() => setVue(choix)}
          style={{
            position: 'relative',
            zIndex: 1,
            flex: 1,
            border: 'none',
            borderRadius: 999,
            padding: '8px 0',
            fontSize: 13.5,
            fontWeight: 800,
            fontFamily: 'inherit',
            background: 'transparent',
            color: vue === choix ? '#FFFFFF' : C.secondaire,
            transition: 'color 0.22s ease-out',
            cursor: 'pointer',
          }}
        >
          {choix === 'planning' ? 'Planning' : choix === 'repas' ? 'Repas' : 'Ma semaine'}
        </button>
      ))}
    </div>
  );

  if (vue === 'semaine') {
    return (
      <div
        style={{
          flex: '1 1 0',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
          padding: '30px 22px 16px 22px',
        }}
      >
        <Titre>Ma semaine</Titre>
        {commutateur}
        <MaSemaine version={version} signaler={signaler} rafraichir={rafraichir} />
      </div>
    );
  }

  if (vue === 'repas') {
    return (
      <div
        style={{
          flex: '1 1 0',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
          padding: '28px 20px 16px 20px',
        }}
      >
        <Titre>Repas</Titre>
        {commutateur}
        <Repas
          fuseau={fuseau}
          version={version}
          signaler={signaler}
          rafraichir={rafraichir}
          ouvrirCompris={ouvrirCompris}
        />
      </div>
    );
  }

  return (
    <div
      onScroll={(e) => setADefile(e.currentTarget.scrollTop > 0)}
      style={{
        flex: '1 1 0',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        padding: '30px 22px 16px 22px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <Titre>Planning</Titre>
        {/* Un MODE, ouvert par un bouton visible. L'appui long aurait ete plus elegant
            et invisible — or c'est exactement ce qui vient de couter une recherche au
            porteur avec le repli des routines. */}
        <button
          type="button"
          className="pressable"
          onClick={() => (selecteur.actif ? selecteur.quitter() : selecteur.ouvrir())}
          style={{
            border: `1px solid ${selecteur.actif ? C.corailBordure : C.bordure}`,
            background: selecteur.actif ? C.corailFond : C.carte,
            color: selecteur.actif ? C.corailTexte : C.secondaire,
            borderRadius: 999,
            padding: '7px 14px',
            fontSize: 12.5,
            fontWeight: 800,
            fontFamily: 'inherit',
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          {selecteur.actif ? 'Annuler' : 'Sélectionner'}
        </button>
      </div>

      {commutateur}

      {/* « Le menu des jours s'affiche tout le temps » : la bande colle en haut du
          defilement et prend une ombre une fois defile.

          EPUREE (handoff § Ecrans 4) : ni cadre, ni barre coloree. Un disque autour du
          seul jour choisi, un point sous chaque jour qui porte quelque chose. Le flou
          derriere laisse deviner le ciel au lieu de le masquer. */}
      <div
        style={{
          display: 'flex',
          gap: 4,
          position: 'sticky',
          top: -30,
          zIndex: 5,
          background: C.bandeCollante,
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          borderBottom: `1px solid ${C.bordure}`,
          margin: '-8px -22px 0',
          padding: '6px 18px 12px',
          boxShadow: aDefile ? '0 6px 16px rgba(10, 8, 30, 0.18)' : 'none',
        }}
      >
        {bande.map((jour) => {
          const { jour: nom, num } = jourAbrege(jour);
          const cest = jour === debut;
          const charge = avecContenu.has(jour);
          return (
            <button
              key={jour}
              type="button"
              className="pressable"
              aria-label={`Aller au ${jourEnToutesLettres(jour)}`}
              aria-current={cest ? 'true' : undefined}
              onClick={() => allerAuJour(jour)}
              style={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 4,
                padding: 0,
                background: 'none',
                border: 'none',
                fontFamily: 'inherit',
                cursor: 'pointer',
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 800,
                  textTransform: 'uppercase',
                  letterSpacing: 0.4,
                  color: C.tertiaire,
                }}
              >
                {nom}
              </div>
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: cest ? C.encre : 'transparent',
                  transition: 'background 0.2s',
                  fontSize: 15,
                  fontWeight: cest ? 800 : charge ? 600 : 400,
                  color: cest ? C.encreInverse : charge ? C.encre : C.tertiaire,
                }}
              >
                {num}
              </div>
              {/* Un point signale un jour qui porte quelque chose : la bande informe, elle
                  ne filtre pas — tous les jours sont listes en dessous. */}
              <span
                style={{
                  width: 4,
                  height: 4,
                  borderRadius: '50%',
                  background: cest
                    ? couleurDuJour(jour)
                    : charge
                      ? C.bordureForte
                      : 'transparent',
                }}
              />
            </button>
          );
        })}
      </div>

      {jours.length === 0 && sansDate.length === 0 && listes.length === 0 ? (
        <Vide
          titre="Rien de prévu"
          texte="Les éléments que tu valides depuis la boîte apparaîtront ici."
        />
      ) : null}

      {jours.map((jour) => (
        <div
          key={jour.date}
          className="entree"
          ref={(el) => {
            if (el === null) refsSections.current.delete(jour.date);
            else refsSections.current.set(jour.date, el);
          }}
          // La section ne doit pas finir SOUS la bande sticky quand on defile vers elle.
          style={{ scrollMarginTop: 76 }}
        >
          <SectionJour
            jour={jour}
            estAujourdhui={jour.date === debut}
            routinesOuvertes={routinesOuvertes}
            basculerRoutines={basculerRoutines}
            selection={selecteur.selection}
            fuseau={fuseau}
            basculer={basculer}
            membres={etat.donnees?.membres ?? []}
            utilisateurId={utilisateurId}
            signaler={signaler}
            rafraichir={rafraichir}
          />
        </div>
      ))}

      {sansDate.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <SousTitreSection>Sans date</SousTitreSection>
          {sansDate.map((tache) => (
            <LigneTache
              key={tache.id}
              // Une tache « Sans date » n'a pas d'echeance : le formulaire de modification
              // permettra justement de lui en donner une.
              tache={{ ...tache, dueAt: null } as unknown as Tache}
              fuseau={fuseau}
              avecHeure={false}
              basculer={basculer}
              membres={etat.donnees?.membres ?? []}
              utilisateurId={utilisateurId}
              signaler={signaler}
              rafraichir={rafraichir}
            />
          ))}
        </div>
      ) : null}

      {listes.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <SousTitreSection>Listes</SousTitreSection>
          {listes.map((liste) => (
            <CarteListe
              key={liste.id}
              liste={liste}
              basculer={basculer}
              signaler={signaler}
              rafraichir={rafraichir}
            />
          ))}
        </div>
      ) : null}

      {selecteur.actif && selecteur.nombre > 0 ? (
        <BarreDeSelection
          nombre={selecteur.nombre}
          cles={selecteur.cles}
          onFini={(message) => {
            selecteur.quitter();
            signaler(message);
            rafraichir();
          }}
          signaler={signaler}
        />
      ) : null}

      {(aVenir.donnees?.items.length ?? 0) > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button
            type="button"
            aria-expanded={plusTardOuvert}
            onClick={() => basculerPlusTard()}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: 'none',
              border: 'none',
              padding: 0,
              fontFamily: 'inherit',
              cursor: 'pointer',
            }}
          >
            <span
              style={{
                fontSize: 12,
                fontWeight: 800,
                letterSpacing: 1,
                textTransform: 'uppercase',
                color: C.secondaire,
              }}
            >
              Plus tard ({aVenir.donnees!.items.length})
            </span>
            <span style={{ fontSize: 13, fontWeight: 800, color: C.violetTexte }}>
              {plusTardOuvert ? 'Replier ▴' : 'Ouvrir ▾'}
            </span>
          </button>
          {plusTardOuvert ? (
            <>
              <div style={{ fontSize: 12, color: C.tertiaire, lineHeight: 1.5, marginTop: -2 }}>
                Tout ce que Colette surveille au-delà des sept prochains jours.
              </div>
              {aVenir.donnees!.items.map((echeance) => (
                <LigneAVenir
                  key={echeance.id}
                  echeance={echeance}
                  aujourdhui={debut}
                  fuseau={fuseau}
                  utilisateurId={utilisateurId}
                  signaler={signaler}
                  rafraichir={rafraichir}
                />
              ))}
            </>
          ) : (
            <div style={{ fontSize: 12, color: C.tertiaire, lineHeight: 1.5, marginTop: -2 }}>
              {aVenir.donnees!.items.length === 1
                ? 'Une échéance surveillée au-delà des sept jours — touche « Ouvrir » pour la voir.'
                : `${aVenir.donnees!.items.length} échéances surveillées au-delà des sept jours — touche « Ouvrir » pour les voir.`}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

/** Une liste generale autonome (fournitures, demarche) : cochable, comme au magasin. */
function CarteListe({
  liste,
  basculer,
  signaler,
  rafraichir,
}: {
  liste: ListeCochable;
  basculer: Basculer;
  signaler: (message: string) => void;
  rafraichir: () => void;
}) {
  return (
    <div
      style={{
        background: C.carte,
        border: `1px solid ${C.bordure}`,
        borderRadius: 14,
        padding: '13px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <EnteteListe liste={liste} signaler={signaler} rafraichir={rafraichir} />
      {liste.elements.map((element) => (
        <LigneElement
          key={element.id}
          element={element}
          basculer={basculer}
          signaler={signaler}
          rafraichir={rafraichir}
        />
      ))}
      <AjouterElement
        checklistId={liste.id}
        titreListe={liste.titre}
        signaler={signaler}
        rafraichir={rafraichir}
      />
    </div>
  );
}

const STYLE_A_VENIR: Record<EcheanceAVenir['type'], { etiquette: string; couleur: string }> = {
  evenement: { etiquette: 'ÉVÉNEMENT', couleur: C.violetTexte },
  tache: { etiquette: 'À FAIRE', couleur: C.secondaire },
  rappel: { etiquette: 'RAPPEL', couleur: C.mentheTexte },
  anniversaire: { etiquette: 'ANNIVERSAIRE', couleur: C.corailTexte },
};

/**
 * Une echeance lointaine : son type, son titre, et dans combien de temps elle tombe.
 *
 * Elle porte aussi de quoi la RETIRER. C'etait une liste morte : pour corriger un
 * rendez-vous dans trois semaines, il fallait attendre trois semaines qu'il entre dans
 * la fenetre du Planning (signale par le porteur le 2026-08-29).
 */
function LigneAVenir({
  echeance,
  aujourdhui,
  fuseau,
  utilisateurId,
  signaler,
  rafraichir,
}: {
  echeance: EcheanceAVenir;
  aujourdhui: string;
  fuseau: string;
  utilisateurId: string;
  signaler: (message: string) => void;
  rafraichir: () => void;
}) {
  const [occupe, setOccupe] = useState(false);
  const [edition, setEdition] = useState(false);
  const style = STYLE_A_VENIR[echeance.type];
  const titre =
    echeance.type === 'anniversaire'
      ? `Anniversaire de ${echeance.titre} (${echeance.age} ans)`
      : echeance.titre;

  // Un anniversaire est CALCULE d'une date de naissance : il n'y a rien a retirer. Le
  // reste appartient a son createur — ou a tout le foyer si personne n'en est l'auteur.
  const monDroit =
    echeance.type !== 'anniversaire' &&
    (echeance.createdByUserId === utilisateurId || echeance.createdByUserId === null);

  const estUneSerie = echeance.serieId !== undefined;
  // JAMAIS l'identifiant composite d'une occurrence : il n'existe dans aucune table.
  const cible = echeance.serieId ?? echeance.id;
  const suffixe = estUneSerie ? ' la série' : '';

  const retirer = async () => {
    if (occupe) return;
    setOccupe(true);
    try {
      const resultat =
        echeance.type === 'tache'
          ? await api.supprimerTache(cible)
          : echeance.type === 'rappel'
            ? await api.supprimerRappel(cible)
            : await api.supprimerEvenement(cible);
      const message = estUneSerie ? 'Série retirée' : 'Retiré';
      // Dire ce qui s'est REELLEMENT passe, Google compris (meme regle qu'au Planning).
      signaler(
        (resultat as { google?: string }).google === 'echec'
          ? `${message} — mais Google Agenda n’a pas suivi`
          : (resultat as { google?: string }).google === 'supprime'
            ? `${message} · supprimé aussi dans Google`
            : message,
      );
      rafraichir();
    } catch (cause) {
      signaler(messageDErreur(cause));
    } finally {
      setOccupe(false);
    }
  };

  const apresModification = async (action: () => Promise<unknown>, message: string) => {
    if (occupe) return;
    setOccupe(true);
    try {
      await action();
      signaler(message);
      setEdition(false);
      rafraichir();
    } catch (cause) {
      signaler(messageDErreur(cause));
    } finally {
      setOccupe(false);
    }
  };

  if (edition) {
    return (
      <div
        style={{
          background: C.carte,
          border: `1px solid ${C.bordure}`,
          borderRadius: 14,
          padding: 13,
          minWidth: 0,
        }}
      >
        {echeance.type === 'tache' ? (
          <FormulaireTache
            tache={{ title: echeance.titre, dueAt: echeance.dueAt ?? '' }}
            fuseau={fuseau}
            occupe={occupe}
            onEnregistrer={(modification) =>
              void apresModification(
                () => api.modifierTache(cible, modification),
                'À faire modifié',
              )
            }
            onAnnuler={() => setEdition(false)}
          />
        ) : echeance.type === 'rappel' ? (
          <FormulaireRappel
            rappel={{ message: echeance.titre, remindAt: echeance.remindAt ?? '' }}
            fuseau={fuseau}
            occupe={occupe}
            onEnregistrer={(modification) =>
              void apresModification(() => api.modifierRappel(cible, modification), 'Rappel modifié')
            }
            onAnnuler={() => setEdition(false)}
          />
        ) : (
          <FormulaireEvenement
            evenement={{
              title: echeance.titre,
              startsAt: echeance.startsAt ?? '',
              endsAt: echeance.endsAt ?? null,
              allDay: echeance.allDay ?? false,
              location: echeance.location ?? null,
            }}
            fuseau={fuseau}
            estUneSerie={estUneSerie}
            occupe={occupe}
            onEnregistrer={(modification) =>
              void apresModification(
                () => api.modifierEvenement(cible, modification),
                estUneSerie ? 'Série modifiée' : 'Événement modifié',
              )
            }
            onAnnuler={() => setEdition(false)}
          />
        )}
      </div>
    );
  }

  return (
    // Les actions vivent SOUS le titre, jamais a cote (correctif du 2026-08-29, sur
    // capture du porteur : « dans 1 semaine » peint par-dessus « Tractage marche »).
    // Mesure : la carte offre 278 px, et « dans 2 semaines » + « Modifier la serie » +
    // « Retirer la serie » en reclament 293. Le titre recevait une largeur NEGATIVE — il
    // ne pouvait que deborder. Meme correctif que la ligne d'evenement du Planning.
    <div
      style={{
        background: C.carte,
        border: `1px solid ${C.bordure}`,
        borderRadius: 14,
        padding: '13px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          <Etiquette couleur={style.couleur}>{style.etiquette}</Etiquette>
          <div style={{ fontSize: 15, fontWeight: 700, color: C.encre }}>{titre}</div>
          <div style={{ fontSize: 12, color: C.secondaire }}>
            {jourEnToutesLettres(echeance.date)}
          </div>
        </div>
        <div style={{ fontSize: 13, fontWeight: 800, color: C.violetTexte, flexShrink: 0 }}>
          {dansCombienDeTemps(echeance.date, aujourdhui)}
        </div>
      </div>
      {monDroit ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <button
            type="button"
            className="pressable"
            disabled={occupe}
            aria-label={`Modifier${suffixe} : ${echeance.titre}`}
            onClick={() => setEdition(true)}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              fontSize: 11.5,
              fontWeight: 800,
              fontFamily: 'inherit',
              color: C.accent,
              cursor: occupe ? 'default' : 'pointer',
            }}
          >
            {estUneSerie ? 'Modifier la série' : 'Modifier'}
          </button>
          <BoutonDeuxGestes
            libelle={estUneSerie ? 'Retirer la série' : 'Retirer'}
            libelleArme="Confirmer ?"
            aria={`Retirer${suffixe} : ${echeance.titre}`}
            ariaArme={`Confirmer le retrait${estUneSerie ? ' de la série' : ''} : ${echeance.titre}`}
            occupe={occupe}
            onConfirmer={() => void retirer()}
          />
        </div>
      ) : null}
    </div>
  );
}

/**
 * Modifier ou retirer un evenement (lot E1) — ce qui etait grave jusqu'ici.
 *
 * Sur une OCCURRENCE de serie, l'action porte sur la serie entiere et le dit : pour un
 * seul jour, « Passer ce jour » existe deja juste a cote.
 */
function ActionsEvenement({
  evenement,
  fuseau,
  signaler,
  rafraichir,
}: {
  evenement: Evenement;
  fuseau: string;
  signaler: (message: string) => void;
  rafraichir: () => void;
}) {
  const [edition, setEdition] = useState(false);
  const [occupe, setOccupe] = useState(false);
  const estUneSerie = evenement.serieId !== undefined;
  const cible = evenement.serieId ?? evenement.id;
  const suffixe = estUneSerie ? ' la série' : '';

  /**
   * Agit, puis DIT CE QUI S'EST REELLEMENT PASSE, Google compris.
   *
   * Les trois cas se distinguent : sans cette distinction, un retrait que Google a
   * refuse ressemblait a un retrait reussi, et un evenement restait dans l'agenda sans
   * que rien ne le signale (constate par le porteur le 2026-08-28).
   *
   * `inactif` n'est PAS un echec : c'est un evenement qui n'etait pas dans Google, ou
   * une ecriture volontairement coupee. On n'en parle donc pas.
   */
  const agir = async (action: () => Promise<{ google?: string }>, message: string) => {
    if (occupe) return;
    setOccupe(true);
    try {
      const resultat = await action();
      signaler(
        resultat.google === 'echec'
          ? `${message} — mais Google Agenda n’a pas suivi`
          : resultat.google === 'supprime'
            ? `${message} · supprimé aussi dans Google`
            : resultat.google === 'ecrit' || resultat.google === 'modifie'
              ? `${message} · Google Agenda mis à jour`
              : message,
      );
      setEdition(false);
      rafraichir();
    } catch (cause) {
      signaler(messageDErreur(cause));
    } finally {
      setOccupe(false);
    }
  };

  if (edition) {
    // `minWidth: 0` n'est pas decoratif : un element flex vaut `min-width: auto` par
    // defaut, donc il refuse de descendre sous la taille de son contenu. Les champs date
    // et heure de Chrome ayant une largeur minimale incompressible, le formulaire SORTAIT
    // de la carte au lieu de se tasser.
    return (
      <div
        style={{
          flexBasis: '100%',
          minWidth: 0,
          background: C.carte,
          borderRadius: 12,
          padding: 12,
        }}
      >
        <FormulaireEvenement
          evenement={evenement}
          fuseau={fuseau}
          estUneSerie={estUneSerie}
          occupe={occupe}
          onEnregistrer={(modification) =>
            void agir(
              () => api.modifierEvenement(cible, modification),
              estUneSerie ? 'Série modifiée' : 'Événement modifié',
            )
          }
          onAnnuler={() => setEdition(false)}
        />
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        className="pressable"
        disabled={occupe}
        aria-label={`Modifier${suffixe} : ${evenement.title}`}
        onClick={() => setEdition(true)}
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          fontSize: 11.5,
          fontWeight: 800,
          fontFamily: 'inherit',
          color: C.violetTexte,
          cursor: occupe ? 'default' : 'pointer',
        }}
      >
        {estUneSerie ? 'Modifier la série' : 'Modifier'}
      </button>
      <BoutonDeuxGestes
        libelle={estUneSerie ? 'Retirer la série' : 'Retirer'}
        libelleArme="Confirmer ?"
        aria={`Retirer${suffixe} : ${evenement.title}`}
        ariaArme={`Confirmer le retrait${estUneSerie ? ' de la série' : ''} : ${evenement.title}`}
        occupe={occupe}
        onConfirmer={() =>
          void agir(
            () => api.supprimerEvenement(cible),
            estUneSerie ? 'Série retirée' : 'Événement retiré',
          )
        }
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          fontSize: 11.5,
          fontWeight: 800,
          fontFamily: 'inherit',
          color: C.tertiaire,
          cursor: occupe ? 'default' : 'pointer',
        }}
        styleArme={{ color: C.corailTexte }}
      />
    </>
  );
}

/**
 * Annuler UNE occurrence d'une serie : deux gestes (« Passer ce jour » puis
 * confirmation), comme tout retrait — un pouce qui glisse ne supprime rien.
 */
function PasserCeJour({
  serieId,
  jour,
  signaler,
  rafraichir,
}: {
  serieId: string;
  jour: string;
  signaler: (message: string) => void;
  rafraichir: () => void;
}) {
  const [arme, setArme] = useState(false);
  const [envoi, setEnvoi] = useState(false);

  const annuler = async () => {
    if (envoi) return;
    setEnvoi(true);
    try {
      await api.annulerOccurrence(serieId, jour);
      signaler('Ce jour est passé — la série continue');
      rafraichir();
    } catch (cause) {
      signaler(messageDErreur(cause));
      setArme(false);
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <button
      type="button"
      disabled={envoi}
      onClick={() => {
        if (arme) void annuler();
        else setArme(true);
      }}
      style={{
        background: 'none',
        border: 'none',
        padding: 0,
        fontSize: 11.5,
        fontWeight: 800,
        fontFamily: 'inherit',
        color: arme ? C.corailTexte : C.tertiaire,
        cursor: envoi ? 'default' : 'pointer',
      }}
    >
      {arme ? 'Confirmer : passer ce jour ?' : 'Passer ce jour'}
    </button>
  );
}

/**
 * L'en-tete d'une liste : son nom, et de quoi le corriger ou retirer la liste entiere.
 * Partage par la section « Listes » du Planning et par l'ecran Repas.
 */
export function EnteteListe({
  liste,
  signaler,
  rafraichir,
  style,
}: {
  liste: ListeCochable;
  signaler: (message: string) => void;
  rafraichir: () => void;
  /** L'ecran Repas titre ses listes en capitales menthe : il passe son propre style. */
  style?: React.CSSProperties;
}) {
  const [edition, setEdition] = useState(false);
  const [nom, setNom] = useState(liste.titre);
  const [occupe, setOccupe] = useState(false);

  const agir = async (action: () => Promise<unknown>, message: string) => {
    if (occupe) return;
    setOccupe(true);
    try {
      await action();
      signaler(message);
      setEdition(false);
      rafraichir();
    } catch (cause) {
      signaler(messageDErreur(cause));
    } finally {
      setOccupe(false);
    }
  };

  if (edition) {
    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const titre = nom.trim();
          if (titre === '' || titre === liste.titre) {
            setEdition(false);
            return;
          }
          void agir(() => api.renommerListe(liste.id, titre), 'Liste renommée');
        }}
        style={{ display: 'flex', gap: 6 }}
      >
        <input
          value={nom}
          onChange={(e) => setNom(e.target.value)}
          aria-label="Nouveau nom de la liste"
          autoFocus
          style={{
            flex: 1,
            minWidth: 0,
            height: 36,
            border: `1px solid ${C.violetBordure}`,
            borderRadius: 10,
            padding: '0 10px',
            fontSize: 16,
            fontFamily: 'inherit',
            color: C.encre,
            // Sans fond declare, un input prend le BLANC du navigateur — et l'encre de
            // Colette etant claire, on n'y voit plus ce qu'on tape.
            background: C.carte,
            outline: 'none',
          }}
        />
        <button
          type="submit"
          className="pressable"
          disabled={occupe}
          style={{
            height: 36,
            padding: '0 12px',
            border: 'none',
            borderRadius: 10,
            background: occupe ? C.inactif : DEGRADE_ACTION,
            color: '#FFFFFF',
            fontSize: 13,
            fontWeight: 800,
            fontFamily: 'inherit',
            cursor: 'pointer',
          }}
        >
          OK
        </button>
        <button
          type="button"
          className="pressable"
          aria-label="Annuler le renommage"
          onClick={() => {
            setNom(liste.titre);
            setEdition(false);
          }}
          style={{
            height: 36,
            padding: '0 10px',
            border: `1px solid ${C.bordure}`,
            borderRadius: 10,
            background: C.carte,
            color: C.secondaire,
            fontSize: 13,
            fontWeight: 700,
            fontFamily: 'inherit',
            cursor: 'pointer',
          }}
        >
          Annuler
        </button>
      </form>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
      <div style={style ?? { fontSize: 15, fontWeight: 700, color: C.encre, flex: 1 }}>
        {style === undefined ? liste.titre : liste.titre.toUpperCase()}
      </div>
      <ActionsLigne
        libelleModifier="Renommer"
        libelleRetirer="Retirer la liste"
        ariaModifier={`Renommer la liste : ${liste.titre}`}
        ariaRetirer={`Retirer la liste : ${liste.titre}`}
        ariaRetirerArme={`Confirmer le retrait de la liste : ${liste.titre}`}
        occupe={occupe}
        onModifier={() => {
          setNom(liste.titre);
          setEdition(true);
        }}
        onRetirer={() => void agir(() => api.supprimerListe(liste.id), 'Liste retirée')}
      />
    </div>
  );
}

/** Un rappel : son heure, son message, son cercle — et de quoi le corriger ou l'effacer. */
function LigneRappel({
  rappel,
  fuseau,
  selection,
  membres,
  utilisateurId,
  signaler,
  rafraichir,
}: {
  rappel: Rappel;
  fuseau: string;
  selection: Selection;
  membres: MembreFoyer[];
  utilisateurId: string;
  signaler: (message: string) => void;
  rafraichir: () => void;
}) {
  const [edition, setEdition] = useState(false);
  const [occupe, setOccupe] = useState(false);

  const agir = async (action: () => Promise<unknown>, message: string) => {
    if (occupe) return;
    setOccupe(true);
    try {
      await action();
      signaler(message);
      setEdition(false);
      rafraichir();
    } catch (cause) {
      signaler(messageDErreur(cause));
    } finally {
      setOccupe(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '4px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <CaseDeSelection
          cle={cleDe('rappel', rappel.id)}
          selection={selection}
          libelle={rappel.message}
        />
        <Cloche taille={18} couleur={C.violet} epaisseur={1.8} />
        <div style={{ fontSize: 14, color: C.encre }}>
          <span style={{ fontWeight: 700 }}>{heureDe(rappel.remindAt, fuseau)}</span> —{' '}
          {rappel.message}
        </div>
      </div>
      <div style={{ paddingLeft: 30 }}>
        {edition ? (
          <FormulaireRappel
            rappel={rappel}
            fuseau={fuseau}
            occupe={occupe}
            onEnregistrer={(modification) =>
              void agir(() => api.modifierRappel(rappel.id, modification), 'Rappel modifié')
            }
            onAnnuler={() => setEdition(false)}
          />
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <InsigneCercle
              genre="rappel"
              id={rappel.id}
              cercle={rappel}
              membres={membres}
              utilisateurId={utilisateurId}
              editable
              signaler={signaler}
              rafraichir={rafraichir}
            />
            <ActionsLigne
              ariaModifier={`Modifier : ${rappel.message}`}
              ariaRetirer={`Retirer : ${rappel.message}`}
              ariaRetirerArme={`Confirmer le retrait : ${rappel.message}`}
              occupe={occupe}
              onModifier={() => setEdition(true)}
              onRetirer={() => void agir(() => api.supprimerRappel(rappel.id), 'Rappel retiré')}
            />
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Une tache cochable : la case et le titre barre quand c'est fait.
 *
 * La carte est un DIV et le geste de cocher un bouton INTERNE : l'insigne de cercle est
 * lui-meme un bouton, et un bouton dans un bouton est du HTML invalide.
 */
function LigneTache({
  tache,
  fuseau,
  avecHeure,
  selection,
  basculer,
  membres,
  utilisateurId,
  signaler,
  rafraichir,
}: {
  tache: Tache;
  fuseau: string;
  /** Les taches « Sans date » n'affichent pas d'heure. */
  avecHeure: boolean;
  /** Absente pour « Sans date » : la selection ne vit que dans les journees. */
  selection?: Selection;
  basculer: Basculer;
  membres: MembreFoyer[];
  utilisateurId: string;
  signaler: (message: string) => void;
  rafraichir: () => void;
}) {
  const [edition, setEdition] = useState(false);
  const [occupe, setOccupe] = useState(false);
  const id = tache.id;
  const titre = tache.title;
  const fait = tache.doneAt !== null;
  const heure = avecHeure && tache.dueAt !== null ? heureDe(tache.dueAt, fuseau) : undefined;

  const agir = async (action: () => Promise<unknown>, message: string) => {
    if (occupe) return;
    setOccupe(true);
    try {
      await action();
      signaler(message);
      setEdition(false);
      rafraichir();
    } catch (cause) {
      signaler(messageDErreur(cause));
    } finally {
      setOccupe(false);
    }
  };

  return (
    <div
      style={{
        background: C.carte,
        border: `1px solid ${C.bordure}`,
        borderRadius: 14,
        padding: '13px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {selection !== undefined ? (
          <CaseDeSelection cle={cleDe('tache', id)} selection={selection} libelle={titre} />
        ) : null}
        <button
          type="button"
          aria-pressed={fait}
          aria-label={`${fait ? 'Décocher' : 'Cocher'} : ${titre}`}
          onClick={() => basculer('tache', id, !fait)}
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            background: 'none',
            border: 'none',
            padding: 0,
            textAlign: 'left',
            fontFamily: 'inherit',
            cursor: 'pointer',
          }}
        >
          <Case cochee={fait} />
          <span
            style={{
              flex: 1,
              fontSize: 15,
              fontWeight: 700,
              color: fait ? C.secondaire : C.encre,
              textDecoration: fait ? 'line-through' : 'none',
            }}
          >
            {titre}
          </span>
          {heure !== undefined ? (
            <span style={{ fontSize: 13, color: C.secondaire }}>{heure}</span>
          ) : null}
        </button>
      </div>
      {edition ? (
        <FormulaireTache
          tache={tache}
          fuseau={fuseau}
          occupe={occupe}
          onEnregistrer={(modification) =>
            void agir(() => api.modifierTache(id, modification), 'Tâche modifiée')
          }
          onAnnuler={() => setEdition(false)}
        />
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <InsigneCercle
            genre="tache"
            id={id}
            cercle={tache}
            membres={membres}
            utilisateurId={utilisateurId}
            editable
            signaler={signaler}
            rafraichir={rafraichir}
          />
          <ActionsLigne
            ariaModifier={`Modifier : ${titre}`}
            ariaRetirer={`Retirer : ${titre}`}
            ariaRetirerArme={`Confirmer le retrait : ${titre}`}
            occupe={occupe}
            onModifier={() => setEdition(true)}
            onRetirer={() => void agir(() => api.supprimerTache(id), 'Tâche retirée')}
          />
        </div>
      )}
    </div>
  );
}

function SectionJour({
  jour,
  estAujourdhui,
  routinesOuvertes,
  basculerRoutines,
  selection,
  fuseau,
  basculer,
  membres,
  utilisateurId,
  signaler,
  rafraichir,
}: {
  jour: JourPlanning;
  /** Aujourd'hui ne se replie jamais tout seul, meme vide. */
  estAujourdhui: boolean;
  /** Le repli des routines est GLOBAL : deplier un jour les deplie tous. */
  routinesOuvertes: boolean;
  basculerRoutines: () => void;
  selection: Selection;
  fuseau: string;
  basculer: Basculer;
  membres: MembreFoyer[];
  utilisateurId: string;
  signaler: (message: string) => void;
  rafraichir: () => void;
}) {
  const couleur = couleurDuJour(jour.date);
  // Les ROUTINES descendent sous les vrais rendez-vous, dans une trame repliee (spec
  // routines, regle 1). Les masquer ferait un planning qui MENT sur la journee ; les
  // mettre au meme niveau reproduirait le mur de trente-cinq lignes.
  const rendezVous = jour.events.filter((e) => e.estUneRoutine !== true);
  const routines = jour.events.filter((e) => e.estUneRoutine === true);

  /**
   * Une journee se replie d'un appui sur son titre (demande du porteur, 2026-08-28).
   *
   * Par defaut : AUJOURD'HUI reste ouvert, une journee VIDE se replie, le reste s'ouvre.
   * Le choix ne survit pas au rechargement — retenir « j'avais plie le 28 aout » n'a
   * aucun sens le 29, puisque ce n'est plus la meme journee.
   */
  const vide =
    rendezVous.length === 0 &&
    routines.length === 0 &&
    jour.tasks.length === 0 &&
    jour.reminders.length === 0 &&
    (jour.birthdays ?? []).length === 0;
  const [replie, setReplie] = useState(vide && !estAujourdhui);

  // Ce que la journee contient, dit en toutes lettres quand elle est repliee : un titre
  // seul serait un mensonge par omission.
  const resume = [
    rendezVous.length > 0
      ? `${rendezVous.length} rendez-vous`
      : null,
    jour.tasks.length > 0 ? `${jour.tasks.length} à faire` : null,
    jour.reminders.length > 0
      ? `${jour.reminders.length} rappel${jour.reminders.length > 1 ? 's' : ''}`
      : null,
    routines.length > 0 ? `${routines.length} routine${routines.length > 1 ? 's' : ''}` : null,
  ]
    .filter((part) => part !== null)
    .join(' · ');

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        borderLeft: `3px solid ${couleur}`,
        paddingLeft: 12,
        borderRadius: 2,
      }}
    >
      <button
        type="button"
        className="pressable"
        aria-expanded={!replie}
        onClick={() => setReplie((r) => !r)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          border: 'none',
          background: 'none',
          padding: '2px 0',
          textAlign: 'left',
          fontFamily: 'inherit',
          cursor: 'pointer',
        }}
      >
        {/* Le titre est BLANC, jamais la teinte du jour. Ces sept teintes ont ete
            choisies pour un lisere de 3 px : ecrites, elles disparaissaient dans le ciel
            — vendredi tombait a 1,03 de contraste, soit la couleur du fond elle-meme
            (signale par le porteur le 2026-08-28, mesure dans test/contraste.spec.ts).
            La teinte reste sur le lisere ci-dessus, qui n'a pas a etre lu. */}
        <span
          style={{
            fontSize: 12,
            fontWeight: 800,
            letterSpacing: 1,
            textTransform: 'uppercase',
            color: C.encre,
          }}
        >
          {jourEnToutesLettres(jour.date)}
        </span>
        {replie && resume !== '' ? (
          <span style={{ fontSize: 12, color: C.tertiaire }}>{resume}</span>
        ) : null}
        <span
          style={{
            display: 'flex',
            transform: replie ? 'none' : 'rotate(180deg)',
            transition: 'transform 0.25s',
          }}
        >
          <ChevronBas taille={12} couleur={C.tertiaire} epaisseur={2.4} />
        </span>
      </button>

      {replie ? null : (
        <>

      {/* Un anniversaire ouvre la journee : c'est la seule chose qu'on ne peut pas rattraper demain. */}
      {(jour.birthdays ?? []).map((anniversaire) => (
        <div
          key={anniversaire.id}
          style={{
            background: C.corailFond,
            borderRadius: 14,
            padding: '14px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <Cadeau taille={22} couleur={C.corailTexte} epaisseur={1.8} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Etiquette couleur={C.corailTexte}>ANNIVERSAIRE</Etiquette>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.encre }}>
              {anniversaire.displayName} fête ses {anniversaire.age} ans
            </div>
          </div>
        </div>
      ))}

      {rendezVous.map((evenement) => (
        <div
          key={evenement.id}
          style={{
            background: C.violetFond,
            borderRadius: 14,
            padding: '14px 16px',
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {/* Ni les occurrences de SERIE (les supprimer emporte la serie entiere) ni
                les evenements GOOGLE (Colette ne les possede pas). */}
            {evenement.serieId === undefined && evenement.provenance !== 'google' ? (
              <CaseDeSelection
                cle={cleDe('evenement', evenement.id)}
                selection={selection}
                libelle={evenement.title}
              />
            ) : null}
            <div style={{ fontSize: 15, fontWeight: 800, color: C.violetTexte, flexShrink: 0 }}>
              {evenement.allDay ? '—' : heureDe(evenement.startsAt, fuseau)}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: C.encre }}>{evenement.title}</div>
              {evenement.location !== null ||
              (evenement.transport ?? null) !== null ||
              evenement.provenance === 'google' ? (
                <div style={{ fontSize: 13, color: C.secondaire }}>
                  {[
                    evenement.location,
                    // Comment on s'y rend, quand la personne l'a dit.
                    evenement.transport ?? null,
                    evenement.provenance === 'google' ? 'Google Agenda' : null,
                  ]
                    .filter((m) => m !== null)
                    .join(' · ')}
                </div>
              ) : null}
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <InsigneCercle
              genre="evenement"
              id={evenement.serieId ?? evenement.id}
              cercle={evenement}
              membres={membres}
              utilisateurId={utilisateurId}
              editable
              signaler={signaler}
              rafraichir={rafraichir}
            />
            {/* Une occurrence de serie s'annule un jour a la fois — par son createur. */}
            {evenement.serieId !== undefined && evenement.createdByUserId === utilisateurId ? (
              <PasserCeJour
                serieId={evenement.serieId}
                jour={jour.date}
                signaler={signaler}
                rafraichir={rafraichir}
              />
            ) : null}
            {/* Un evenement venu de Google se gere dans Google : Colette ne le possede
                pas (il n'a ni cercle ni createur de ce cote). */}
            {evenement.provenance !== 'google' && evenement.visibility !== undefined ? (
              <ActionsEvenement
                evenement={evenement}
                fuseau={fuseau}
                signaler={signaler}
                rafraichir={rafraichir}
              />
            ) : null}
          </div>

          {evenement.checklists.map((liste) => (
            <div
              key={liste.id}
              style={{
                background: C.carte,
                borderRadius: 10,
                padding: '10px 12px',
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
              }}
            >
              {/* Une liste rattachee a un evenement se renomme et se retire comme les
                  autres : elle porte les memes gestes, avec sa forme a elle. */}
              <EnteteListe
                liste={{
                  id: liste.id,
                  titre: liste.title,
                  elements: liste.items,
                }}
                signaler={signaler}
                rafraichir={rafraichir}
                style={{ fontSize: 12, fontWeight: 800, color: C.violetTexte, flex: 1 }}
              />
              {liste.items.map((element) => (
                <LigneElement
                  key={element.id}
                  element={element}
                  basculer={basculer}
                  signaler={signaler}
                  rafraichir={rafraichir}
                />
              ))}
              <AjouterElement
                checklistId={liste.id}
                titreListe={liste.title}
                signaler={signaler}
                rafraichir={rafraichir}
              />
            </div>
          ))}
        </div>
      ))}

      {jour.tasks.map((tache) => (
        <LigneTache
          key={tache.id}
          tache={tache}
          fuseau={fuseau}
          avecHeure
          selection={selection}
          basculer={basculer}
          membres={membres}
          utilisateurId={utilisateurId}
          signaler={signaler}
          rafraichir={rafraichir}
        />
      ))}

      {jour.reminders.map((rappel) => (
        <LigneRappel
          key={rappel.id}
          rappel={rappel}
          fuseau={fuseau}
          selection={selection}
          membres={membres}
          utilisateurId={utilisateurId}
          signaler={signaler}
          rafraichir={rafraichir}
        />
      ))}

      <TrameDesRoutines
            routines={routines}
            fuseau={fuseau}
            ouverte={routinesOuvertes}
            basculer={basculerRoutines}
          />
        </>
      )}
    </div>
  );
}

/**
 * Les routines du jour, en trame repliee sous les vrais rendez-vous.
 *
 * En LECTURE seulement : on les gere dans « Ma semaine », pas ici. Un geste de
 * modification a cet endroit laisserait croire qu'on ne touche qu'a ce jour-la, alors
 * qu'une routine est une serie.
 */
function TrameDesRoutines({
  routines,
  fuseau,
  ouverte,
  basculer,
}: {
  routines: Evenement[];
  fuseau: string;
  ouverte: boolean;
  basculer: () => void;
}) {
  if (routines.length === 0) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <button
        type="button"
        aria-expanded={ouverte}
        onClick={basculer}
        className="pressable"
        style={{
          alignSelf: 'flex-start',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          // Un contour plutot qu'un texte gris perdu : c'est ce qui manquait pour le
          // trouver du regard.
          border: `1px solid ${C.bordure}`,
          background: C.carteDouce,
          borderRadius: 999,
          padding: '5px 12px',
          fontSize: 12,
          fontWeight: 800,
          fontFamily: 'inherit',
          color: C.secondaire,
          cursor: 'pointer',
        }}
      >
        {routines.length} routine{routines.length > 1 ? 's' : ''}
        <span
          style={{
            display: 'flex',
            transform: ouverte ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.25s',
          }}
        >
          <ChevronBas taille={12} couleur={C.secondaire} epaisseur={2.4} />
        </span>
      </button>

      {ouverte
        ? routines
            .slice()
            .sort((a, b) => a.startsAt.localeCompare(b.startsAt))
            .map((routine) => (
              <div
                key={routine.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '6px 10px',
                  borderRadius: 10,
                  background: C.carteDouce,
                }}
              >
                <span style={{ fontSize: 12, fontWeight: 800, color: C.tertiaire, width: 42 }}>
                  {heureDe(routine.startsAt, fuseau)}
                </span>
                <span style={{ fontSize: 13, color: C.secondaire }}>{routine.title}</span>
              </div>
            ))
        : null}
    </div>
  );
}

/**
 * La barre qui apparait des qu'une chose est cochee : combien, et le geste pour les
 * effacer d'un coup.
 *
 * UNE confirmation pour tout le lot, pas une par ligne : cinq confirmations d'affilee
 * seraient pires que pas de garde-fou du tout — on les enchainerait sans les lire.
 *
 * Les suppressions partent EN SERIE, jamais en parallele : chacune peut declencher un
 * retrait dans Google, et lancer dix appels ensemble ferait courir autant de
 * transactions sur le meme foyer (meme regle que « Tout valider »).
 */
function BarreDeSelection({
  nombre,
  cles,
  onFini,
  signaler,
}: {
  nombre: number;
  cles: string[];
  onFini: (message: string) => void;
  signaler: (message: string) => void;
}) {
  const [occupe, setOccupe] = useState(false);

  const supprimer = async () => {
    if (occupe) return;
    setOccupe(true);
    let googleAEchoue = false;
    try {
      for (const cle of cles) {
        const [genre, id] = cle.split(':') as [GenreSelectionnable, string];
        if (genre === 'evenement') {
          const resultat = await api.supprimerEvenement(id);
          if (resultat?.google === 'echec') googleAEchoue = true;
        } else if (genre === 'tache') {
          await api.supprimerTache(id);
        } else {
          await api.supprimerRappel(id);
        }
      }
      const quoi = `${nombre} élément${nombre > 1 ? 's' : ''} retiré${nombre > 1 ? 's' : ''}`;
      // Dire ce que Google a fait, comme le retrait unitaire.
      onFini(googleAEchoue ? `${quoi} — mais Google Agenda n’a pas suivi` : quoi);
    } catch (cause) {
      signaler(messageDErreur(cause));
      setOccupe(false);
    }
  };

  return (
    <div
      className="apparition"
      style={{
        position: 'sticky',
        bottom: 8,
        zIndex: 6,
        background: C.bandeCollante,
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        border: `1px solid ${C.bordureForte}`,
        borderRadius: 16,
        padding: '10px 12px 10px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
      }}
    >
      <div style={{ flex: 1, fontSize: 13.5, fontWeight: 800, color: C.encre }}>
        {nombre} sélectionné{nombre > 1 ? 's' : ''}
      </div>
      <BoutonDeuxGestes
        libelle="Supprimer"
        libelleArme={`Supprimer les ${nombre} ?`}
        aria={`Supprimer les ${nombre} éléments sélectionnés`}
        ariaArme={`Confirmer la suppression des ${nombre} éléments`}
        onConfirmer={() => void supprimer()}
        occupe={occupe}
        style={{
          border: `1px solid ${C.corailBordure}`,
          background: C.corailFond,
          color: C.corailTexte,
          borderRadius: 999,
          padding: '8px 14px',
          fontSize: 12.5,
          fontWeight: 800,
          fontFamily: 'inherit',
          cursor: occupe ? 'default' : 'pointer',
          flexShrink: 0,
        }}
        styleArme={{ background: C.corailTexte, color: '#FFFFFF' }}
      />
    </div>
  );
}
